import type { CookieHelpers } from './cookie';
import { isMobile } from './isMobile';

export type AppClientConfig = {
	clientId: string;
	apiKey: string;
	deviceId?: string;
	uuid?: string;
	appVersion?: string;
	at?: string;
};

export function getCommonHeaders(config: AppClientConfig) {
	const { deviceId, uuid, appVersion, at, clientId, apiKey } = config;
	const headers: Record<string, string> = {
		apiKey,
		clientId,
		ixiSrc: clientId,
	};

	if (deviceId) {
		headers.deviceId = deviceId;
	}
	if (appVersion) {
		headers.appVersion = appVersion;
	}
	const shouldSetUUID = !at;
	if (at) {
		headers.Authorization = `Bearer ${at}`;
	}
	if (shouldSetUUID) {
		if (uuid) {
			headers.uuid = uuid;
		} else if (deviceId) {
			headers.uuid = deviceId;
		}
	}
	return headers;
}

export function getRequestContext({
	clientId: preferredClientId,
	getCookie,
	ua,
}: {
	clientId?: string;
	getCookie: (key: string) => string | undefined;
	ua: string;
}): AppClientConfig {
	const CONFIG = {
		apiKey: 'ixiweb!2$',
		clientId: 'ixiweb',
	};

	const CONFIG_mobile = {
		apiKey: 'iximweb!2$',
		clientId: 'iximweb',
	};

	const allCookies = [
		'at',
		'clientId',
		'apiKey',
		'uuid',
		'appVersion',
		'ixiUID',
	].reduce(
		(acc, key) => {
			acc[key] = getCookie(key);
			return acc;
		},
		{} as Record<string, string | undefined>,
	);

	const {
		ixiUID: deviceId,
		clientId,
		apiKey,
		uuid,
		appVersion,
		at,
	} = allCookies as Record<string, string>;

	const isMobileDevice = isMobile(ua);

	const clientConfig = isMobileDevice
		? {
				clientId: CONFIG_mobile.clientId,
				apiKey: CONFIG_mobile.apiKey,
			}
		: CONFIG;

	const client = preferredClientId
		? {
				clientId: preferredClientId,
				apiKey: `${preferredClientId}!2$`,
			}
		: clientId
			? {
					clientId: clientId,
					apiKey: apiKey || `${clientId}!2$`,
				}
			: clientConfig;

	return {
		deviceId,
		...client,
		uuid,
		appVersion,
		at,
	};
}

/**
 * @param cookieStore - cookie store to use for storing the ixiUID
 * @returns a function that returns the ixiUID
 */
export function ixiUIDResolver(cookieStore: CookieHelpers) {
	let ixiUIDPromise: Promise<string> | undefined;
	return async () => {
		const ixiUID = cookieStore.getCookie('ixiUID');
		if (ixiUID) return ixiUID;
		if (!ixiUIDPromise) {
			ixiUIDPromise = fetch(
				'https://edge-dyn.ixigo.com/eJwzdTE1cXEyNTOwMDExMXB0dnR0daxx/userSource.js',
				{ credentials: 'include' },
			)
				.then((res) => {
					return res.text();
				})
				.catch(() => {
					// Check if running on localhost
					const isLocalhost =
						typeof window !== 'undefined' &&
						(window.location.hostname === 'localhost' ||
							window.location.hostname === '127.0.0.1');
					if (!isLocalhost) {
						return '';
					}
					const randomValue = Array.from(
						crypto.getRandomValues(new Uint8Array(15)),
					)
						.map((byte) => byte.toString(36))
						.join('')
						.slice(0, 20);
					cookieStore.setCookie('ixiUID', randomValue);
					return randomValue;
				})
				.finally(() => {
					cookieStore.syncCookies();
				});
		}
		await ixiUIDPromise;
		return cookieStore.getCookie('ixiUID');
	};
}

export interface AppContext {
	clientConfig: () => AppClientConfig;
	cookie: CookieHelpers;

	/**
	 * available only on the server side
	 */
	_theme?: {
		/**
		 * Precomputed CSS for theming. Injected on the server and reused on the client during hydration.
		 */
		style?: string;

		/**
		 * script to detect theme preference and set the theme initially to avoid paint flashing
		 */
		script?: string;
	};
}
